/**
 * Demo scenarios — named synthetic-fleet presets. Each preset seeds an editable
 * working copy in the planner store (count, tier mix, arrival/departure curves,
 * seed). Built-ins ship here; user-saved customs live in the store (localStorage).
 */
import type { SyntheticOptions } from './syntheticInquirySource'
import {
  DEFAULT_TIER_WEIGHTS,
  DEFAULT_ARRIVAL_DAY_WEIGHTS,
  DEFAULT_DEP_DAY_WEIGHTS,
  DEFAULT_SEED,
} from './syntheticInquirySource'

export interface DemoScenario {
  id: string
  name: string
  description: string
  options: SyntheticOptions
  /** Marks user-saved scenarios (deletable). Built-ins omit this. */
  custom?: boolean
}

export const DEFAULT_SCENARIO_ID = 'baseline'

export const DEMO_SCENARIOS: Record<string, DemoScenario> = {
  baseline: {
    id: 'baseline',
    name: 'Masters Standard',
    description: '~400 aircraft — standard surge fleet mix, back-loaded arrival ramp, Sunday exodus.',
    options: {
      count: 400,
      seed: DEFAULT_SEED,
      tierWeights: { ...DEFAULT_TIER_WEIGHTS },
      arrivalDayWeights: [...DEFAULT_ARRIVAL_DAY_WEIGHTS],
      depDayWeights: DEFAULT_DEP_DAY_WEIGHTS.map((p) => [...p] as [number, number]),
    },
  },
  surge: {
    id: 'surge',
    name: 'Heavy Surge',
    description: '600 aircraft skewed to larger jets — a peak-demand stress test.',
    options: {
      count: 600,
      seed: DEFAULT_SEED,
      tierWeights: { heavy: 22, super_mid: 28, mid: 24, light: 16, turboprop: 8, piston: 2 },
      arrivalDayWeights: [...DEFAULT_ARRIVAL_DAY_WEIGHTS],
      depDayWeights: DEFAULT_DEP_DAY_WEIGHTS.map((p) => [...p] as [number, number]),
    },
  },
  offpeak: {
    id: 'offpeak',
    name: 'Off-Peak',
    description: '120 aircraft, light-skewed mix, flatter arrival/departure spread.',
    options: {
      count: 120,
      seed: DEFAULT_SEED,
      tierWeights: { heavy: 4, super_mid: 10, mid: 18, light: 34, turboprop: 22, piston: 12 },
      arrivalDayWeights: [6, 7, 8, 9, 10, 11, 12, 10, 8, 6, 5, 4],
      depDayWeights: [
        [6, 12],
        [8, 18],
        [9, 22],
        [10, 20],
        [11, 14],
      ],
    },
  },
  vip_heavy: {
    id: 'vip_heavy',
    name: 'VIP Heavy',
    description: '300 aircraft dominated by heavy / super-mid jets — premium clientele.',
    options: {
      count: 300,
      seed: DEFAULT_SEED,
      tierWeights: { heavy: 34, super_mid: 34, mid: 18, light: 10, turboprop: 3, piston: 1 },
      arrivalDayWeights: [...DEFAULT_ARRIVAL_DAY_WEIGHTS],
      depDayWeights: DEFAULT_DEP_DAY_WEIGHTS.map((p) => [...p] as [number, number]),
    },
  },
}
